/**
 * GW-8468: Filter AWS RUM telemetry noise from Sentry.
 *
 * The AWS CloudWatch RUM SDK (aws-rum-web) fails to fetch Cognito identity/OpenId
 * tokens for its telemetry beacon on flaky client networks / adblockers / mobile Safari,
 * throwing unhandled rejections tagged 'CWR: ...' that Sentry captures as errors.
 * These have zero app impact but pollute the Sentry issue queue.
 */

/**
 * Check if an error is from AWS RUM telemetry noise (CWR: prefix or aws-rum-web stack frame).
 * Returns true if the error should be filtered out (dropped from Sentry).
 */
export function isAwsRumTelemetryNoise(originalException: unknown): boolean {
  // Handle non-Error objects gracefully
  if (!originalException || typeof originalException !== 'object') {
    return false;
  }

  // Check for CWR: prefix in error message
  if ('message' in originalException && typeof originalException.message === 'string') {
    if (originalException.message.startsWith('CWR:')) {
      return true;
    }
  }

  // Check for an aws-rum-web stack frame. The AC names the frame
  // aws-rum-web/dist/es/dispatch/CognitoIdentityClient.js — matching on 'aws-rum-web'
  // catches it while staying scoped to the RUM SDK. We deliberately do NOT match a
  // bare 'CognitoIdentityClient.js': the app also bundles @aws-sdk/nested-clients,
  // whose own CognitoIdentityClient.js drives genuine app Cognito flows, and AC2
  // requires those errors to still reach Sentry.
  if ('stack' in originalException && typeof originalException.stack === 'string') {
    if (originalException.stack.includes('aws-rum-web')) {
      return true;
    }
  }

  return false;
}

/**
 * GW-9112: Check if an error is a transient "Failed to fetch" TypeError.
 *
 * The browser throws a bare `TypeError: Failed to fetch` when a network request
 * is interrupted (flaky connection, adblocker, tab backgrounding). Sentry/RUM
 * auto-instrumentation (aws-rum-web/@sentry) taps the original fetch promise
 * independently of our app code and surfaces the rejection to
 * window.onunhandledrejection, so fetchWithNetworkGuard (GW-8963) cannot suppress
 * it. These carry no actionable app signal but flood the Sentry issue queue.
 *
 * Returns true only for the exact `Failed to fetch` TypeError so genuine app
 * errors (other TypeErrors, DOMExceptions, etc.) still reach Sentry.
 * `instanceof TypeError` already returns false for null/undefined/non-objects,
 * so no extra defensive guard is required.
 */
export function isTransientFetchError(originalException: unknown): boolean {
  return originalException instanceof TypeError && originalException.message === 'Failed to fetch';
}
